
import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Session } from '@supabase/supabase-js';
import { Router } from '@angular/router';

export interface Reviewer {
    id: string;
    fullName: string;
    email: string;
    isReviewer: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private supabase = inject(SupabaseService);
    private router: Router = inject(Router);

    #session = signal<Session | null>(null);
    #initialSessionResolved = signal(false);

    initialSessionResolved = this.#initialSessionResolved.asReadonly();
    
    currentUser = computed<Reviewer | null>(() => {
        console.log('[AuthService] currentUser computed signal running...');
        const session = this.#session();
        if (session?.user) {
            console.log('[AuthService] Session found, checking app_metadata:', session.user.app_metadata);
            const user = session.user;
            const isReviewer = user.app_metadata?.onboard_reviewer === true;
            console.log(`[AuthService] Is user a reviewer? ${isReviewer}`);
            if (!isReviewer) {
              console.log('[AuthService] User is not a reviewer, returning null for currentUser.');
              return null;
            }
            const reviewerData = {
                id: user.id,
                fullName: user.user_metadata?.full_name || user.email || 'Reviewer',
                email: user.email || '',
                isReviewer: isReviewer,
            };
            console.log('[AuthService] Reviewer data constructed:', reviewerData);
            return reviewerData;
        }
        console.log('[AuthService] No session found, returning null for currentUser.');
        return null;
    });
    
    // Is the user authorized as a reviewer?
    isAuthenticated = computed(() => !!this.currentUser());
    
    // Is the user logged in at all?
    isLoggedIn = computed(() => !!this.#session());

    // Is the user in the specific state of being logged in but not a reviewer?
    isLoggedInButNotReviewer = computed(() => this.isLoggedIn() && !this.isAuthenticated());


    constructor() {
        console.log('[AuthService] Initializing...');
        this.supabase.authStateChanges((event, session) => {
            console.log('[AuthService] Auth state changed:', { event, session });
            this.#session.set(session);

            // Signal that the first auth event has been processed, allowing the router to start.
            if (!this.#initialSessionResolved()) {
                console.log('[AuthService] Initial auth check complete. Allowing router to navigate.');
                this.#initialSessionResolved.set(true);
            }
        });
        
        // Redirect logic when auth state changes
        effect(() => {
            if (!this.#initialSessionResolved()) return; // Wait for Supabase to restore session before redirecting

            const authed = this.isAuthenticated();
            const notReviewer = this.isLoggedInButNotReviewer();
            const onLoginPage = this.router.url.includes('/login');
            console.log('[AuthService] Routing effect triggered:', {
                url: this.router.url,
                isAuthenticated: authed,
                isLoggedInButNotReviewer: notReviewer,
                onLoginPage: onLoginPage
            });

            if (authed && onLoginPage) {
                console.log('[AuthService] User is authenticated and on login page, redirecting to /applications');
                this.router.navigate(['/applications']);
            } else if (!authed && !onLoginPage && !notReviewer) {
                // Only redirect to login if they are truly logged out, not just unauthorized.
                // This prevents the redirect loop.
                console.log('[AuthService] User is not authenticated, not on login page, and not in "unauthorized" state. Redirecting to /login.');
                this.router.navigate(['/login']);
            }
        }, { allowSignalWrites: true });
    }
}
